import type { GameRoomSummary, Sport } from '@shared/types';

interface RoomCardProps {
  room: GameRoomSummary;
  onJoin: (roomId: string) => void;
}

/**
 * Tarjeta de sala activa del lobby (estilo "Players inside" de Roblox).
 * Muestra nombre, deporte, jugadores actuales, host y estado del lobby.
 * Se actualiza en tiempo real vía `rooms:list` del WebSocket.
 */
export const RoomCard = ({ room, onJoin }: RoomCardProps) => {
  const full = room.currentPlayers >= room.maxPlayers;
  return (
    <article className="card-surface flex items-center gap-4 p-4 transition hover:border-accent-500/40">
      {/* Thumbnail mini del estadio */}
      <div className="grid h-16 w-16 shrink-0 place-items-center rounded-xl bg-pitch-gradient">
        <span className="text-2xl">{sportEmoji(room.sport)}</span>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h4 className="min-w-0 truncate font-display text-base font-bold text-white">{room.name}</h4>
          {room.isPrivate && (
            <span className="pill bg-amber-500/20 text-amber-300">Privada</span>
          )}
          <StatusBadge status={room.status} />
        </div>
        <p className="mt-0.5 text-xs text-slate-400">
          Host: <span className="text-slate-200">{room.hostUsername}</span>
          {room.stadiumName && (
            <>
              {' · '}
              <span className="text-slate-200">{room.stadiumName}</span>
            </>
          )}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400">
          <span>
            <strong className="text-white">{room.currentPlayers}</strong>
            {' / '}
            {room.maxPlayers} jugadores
          </span>
          <div className="h-1.5 w-32 overflow-hidden rounded-full bg-ink-700">
            <div
              className="h-full rounded-full bg-accent-500 transition-all"
              style={{ width: `${(room.currentPlayers / room.maxPlayers) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <button
        disabled={full}
        onClick={() => onJoin(room.id)}
        className="btn-primary h-9 shrink-0 px-4 text-xs"
      >
        {full ? 'Llena' : 'Unirse'}
      </button>
    </article>
  );
};

const StatusBadge = ({ status }: { status: GameRoomSummary['status'] }) => {
  const map: Record<GameRoomSummary['status'], { label: string; cls: string }> = {
    waiting: { label: 'Esperando', cls: 'bg-neon-green/20 text-neon-green' },
    ready: { label: 'Listos', cls: 'bg-accent-500/20 text-accent-400' },
    in_game: { label: 'En partida', cls: 'bg-neon-orange/20 text-neon-orange' },
    closed: { label: 'Cerrada', cls: 'bg-neon-red/20 text-neon-red' },
  };
  const { label, cls } = map[status];
  return <span className={`pill ${cls}`}>{label}</span>;
};

const SPORT_EMOJI: Record<Sport, string> = {
  futbol: '⚽',
  beisbol: '⚾',
  basquetbol: '🏀',
};

const sportEmoji = (s: Sport): string => SPORT_EMOJI[s];